import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from '@/hooks/use-toast'
import { useResyncFeedback } from '@/hooks/useFeedback'
import { ApiError } from '@/lib/api-error'
import type { FeedbackReportDetail, GitHubSync } from '@/types/feedback'

// A pending sync may have lost its job: the API tells whether it is stuck yet.
const RESYNCABLE: ReadonlySet<GitHubSync | null> = new Set(['failed', 'pending'])

function resyncErrorKey(error: unknown): string {
  if (!(error instanceof ApiError) || error.status !== 409) return 'detail.diagnostics.resyncError'
  // Any other 409, one without a reason included (an API on the previous
  // release), is a sync that cannot be queued again yet.
  return error.reason === 'github_sync_disabled'
    ? 'detail.diagnostics.resyncDisabled'
    : 'detail.diagnostics.resyncTooEarly'
}

/** The replay id is appended to the base, so the base must end with a slash. */
function withTrailingSlash(base: string | undefined): string | undefined {
  if (!base) return undefined
  return base.endsWith('/') ? base : `${base}/`
}

/** Platform admins only: the API sends these fields as null to everyone else. */
export function DiagnosticsPanel({ report }: { report: FeedbackReportDetail }) {
  const { t } = useTranslation('feedback')
  const replayBase = withTrailingSlash(import.meta.env.VITE_SENTRY_REPLAYS_URL)
  const resync = useResyncFeedback(report.id)
  // Replies that never reached the issue: the resync queues them again.
  const waitingReplies = report.messages.filter(
    (message) => message.author === 'user' && RESYNCABLE.has(message.github_sync),
  ).length
  const canResync = RESYNCABLE.has(report.github_sync) || waitingReplies > 0

  const handleResync = () =>
    resync.mutate(undefined, {
      onError: (error) => toast({ variant: 'destructive', title: t(resyncErrorKey(error)) }),
    })

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('detail.diagnostics.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2">
          <dt className="text-muted-foreground">{t('detail.diagnostics.replay')}</dt>
          <dd>
            {report.sentry_replay_id ? (
              replayBase ? (
                <a
                  href={`${replayBase}${encodeURIComponent(report.sentry_replay_id)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  {t('detail.diagnostics.openReplay')}
                </a>
              ) : (
                <code>{report.sentry_replay_id}</code>
              )
            ) : (
              t('detail.diagnostics.noReplay')
            )}
          </dd>
          <dt className="text-muted-foreground">{t('detail.diagnostics.issue')}</dt>
          <dd>
            {report.github_issue_number
              ? `#${report.github_issue_number}`
              : t('detail.diagnostics.noIssue')}
          </dd>
          <dt className="text-muted-foreground">{t('detail.diagnostics.sync')}</dt>
          <dd className="flex items-center gap-3">
            <code>{report.github_sync}</code>
            {canResync && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleResync}
                disabled={resync.isPending}
              >
                {t('detail.diagnostics.resync')}
              </Button>
            )}
          </dd>
          {waitingReplies > 0 && (
            <>
              <dt className="text-muted-foreground">{t('detail.diagnostics.waitingReplies')}</dt>
              <dd>{waitingReplies}</dd>
            </>
          )}
        </dl>
        <div>
          <p className="mb-1 text-muted-foreground">{t('detail.diagnostics.context')}</p>
          <pre className="max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs">
            {JSON.stringify(report.context, null, 2)}
          </pre>
        </div>
      </CardContent>
    </Card>
  )
}
